import { Component, OnInit } from '@angular/core';
import { FooterComponent } from '../../core/layout/components/footer/footer.component';
import { ToolbarComponent } from '../../core/layout/components/toolbar/toolbar.component';
import { NgxOrgChartComponent } from '@ahmedaoui/ngx-org-chart';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatStepperModule } from '@angular/material/stepper';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DecideService } from './services/decide.service';
import { IDecision, IMock } from '../interfaces/decision.interface';

@Component({
  selector: 'app-decide',
  standalone: true,
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
  imports: [
    FormsModule,
    FormsModule,
    CommonModule,
    MatIconModule,
    MatRadioModule,
    MatButtonModule,
    FooterComponent,
    ToolbarComponent,
    MatTooltipModule,
    MatStepperModule,
    MatStepperModule,
    ReactiveFormsModule,
    NgxOrgChartComponent,
  ],
  templateUrl: './decide.component.html',
  styleUrl: './decide.component.scss',
})
export class DecideComponent implements OnInit {
  // ORGANIZATION CHART
  nodes: any[] = [];
  selectedStaffCount: number = 0;
  zoom = 1;

  // LIST OF DECISIONS
  selectedDecision: IDecision | null = null;
  decisionList: IDecision[] = [];

  // CREDIBILITY
  progressBars = Array(10).fill(Math.random());
  credibility = 7;

  // USEFUL WEEKS
  dashArray: string = '';
  dashOffset: string = '';
  weeksProgress: number = 0;
  maxWeeks: number = 0;

  // CRITICAL MASS
  progressCriticalMass = Array(20).fill(Math.random());
  criticalMass = 0;

  // ORGANIZATION ADOPTION STAGE
  stepperStatus = [false, false, false];

  constructor(private service: DecideService) {}

  data!: IMock;

  ngOnInit(): void {
    this.service.decisionsSubject.subscribe((data) => {
      if (data) {
        this.maxWeeks = data.scenario.total_weeks;
        this.data = data;
        this.nodes = [data.organisation_chart];
        this.decisionList = data.decisions;
        this.updateSelectedCount();
        this.customizeOrgChart();
        this.setProgressUsefulWeeks(0);
      }
    });
  }

  takeAction() {
    const isValid = this.validateNecessaryStaffCount();
    if (!isValid) {
      return;
    }

    const toggleCssClass = (nodes: any[]): void => {
      nodes.forEach((node) => {
        node.title = node.job_title;
        node.title = node.job_title + ' || ' + node.stage_name.toUpperCase();
        node.stage_name = 'Awareness';

        if (node.childs && node.childs.length > 0) {
          toggleCssClass(node.childs);
        }

        //DEFINE USER STAGE
        if (node.points < 6) {
          node.stage_name = 'Unawareness';
        } else if (node.points >= 6 && node.points < 20) {
          node.stage_name = 'Awareness';
        } else if (node.points >= 20 && node.points < 30) {
          node.stage_name = 'Interest';
        } else if (node.points >= 30 && node.points < 40) {
          node.stage_name = 'Experimentation';
        } else if (node.points >= 40 && node.points < 50) {
          node.stage_name = 'Adoption';
        }
      });
    };

    toggleCssClass(this.nodes);

    this.defineOrganizationAdoptionStage();

    this.weeksProgress =
      this.weeksProgress + (this.selectedDecision?.implementation_weeks ?? 0);
    this.setProgressUsefulWeeks(this.weeksProgress);
    this.credibility = this.credibility + 1;
    this.clearOrgChart();
  }

  validateNecessaryStaffCount() {
    if (
      this.selectedDecision?.name == 'Communicate the reason for change' &&
      this.selectedStaffCount < 20
    ) {
      alert('Please select 20 staff');
      return false;
    } else if (
      this.selectedDecision?.name ==
        'Connect with Key Stakeholders and address initial concerns' &&
      this.selectedStaffCount < 3
    ) {
      alert('Please select 3 staff');
      return false;
    } else if (
      this.selectedDecision?.name == 'Give recognition to an adopter' &&
      this.selectedStaffCount < 1
    ) {
      alert('Please select 1 staff');
      return false;
    }

    return true;
  }

  onDecisionChange(event: any): void {
    const selectedDecision = event.value; // The selected decision object

    // Perform any additional logic here
    if (this.selectedDecision?.name == 'Communicate the reason for change') {
      this.triggerAll();
    } else if (
      this.selectedDecision?.name ==
      'Connect with Key Stakeholders and address initial concerns'
    ) {
    }
  }

  clearOrgChart() {
    const toggleCssClass = (nodes: any[]): void => {
      nodes.forEach((node) => {
        node.cssClass = '';
        node.title = node.job_title + ' || ' + node.stage_name.toUpperCase();

        if (node.childs && node.childs.length > 0) {
          toggleCssClass(node.childs);
        }
      });
    };

    toggleCssClass(this.nodes);
    this.selectedStaffCount = 0;
    this.selectedDecision = null;
  }

  // ORGANIZATION ADOPTION STAGE
  defineOrganizationAdoptionStage() {
    const stageCounts = this.countStageNamesInNodes(this.nodes);

    const mobilization =
      (stageCounts['Awareness'] ?? 0) +
      (stageCounts['Interest'] ?? 0) +
      (stageCounts['Experimentation'] ?? 0) +
      (stageCounts['Adoption'] ?? 0);

    const movement =
      (stageCounts['Interest'] ?? 0) +
      (stageCounts['Experimentation'] ?? 0) +
      (stageCounts['Adoption'] ?? 0);

    const support = stageCounts['Adoption'] ?? 0;

    this.criticalMass = support;

    if ((support / 20) * 100 >= 80) {
      this.stepperStatus.fill(true, 0, 3);
    } else if ((movement / 20) * 100 >= 60 && (movement / 20) * 100 < 80) {
      this.stepperStatus.fill(true, 0, 2);
    } else if ((mobilization / 20) * 100 >= 40) {
      this.stepperStatus.fill(true, 0, 1);
    }
  }

  countStageNamesInNodes = (nodes: any[]): Record<string, number> => {
    const stageCounts: Record<string, number> = {};

    const traverseNodes = (nodes: any[]): void => {
      nodes.forEach((node) => {
        // Initialize the counter for the stage_name if it doesn't exist
        if (!stageCounts[node.stage_name]) {
          stageCounts[node.stage_name] = 0;
        }
        // Increment the counter for the corresponding stage_name
        stageCounts[node.stage_name]++;

        // Recursively traverse child nodes if they exist
        if (node.childs && node.childs.length > 0) {
          traverseNodes(node.childs);
        }
      });
    };

    traverseNodes(nodes);
    return stageCounts;
  };

  // USEFUL WEEKS
  setProgressUsefulWeeks(newProgress: number): void {
    this.weeksProgress = newProgress;
    this.updateProgressUsefulWeeks();
  }

  setMaxUsefulWeeks(newMax: number): void {
    this.maxWeeks = newMax;
    this.updateProgressUsefulWeeks();
  }

  updateProgressUsefulWeeks(): void {
    const clampedProgress = Math.min(
      Math.max(this.weeksProgress, 0),
      this.maxWeeks
    );
    const percentage = clampedProgress / this.maxWeeks;
    const circumference = 40 * Math.PI;
    this.dashArray = `${circumference} ${circumference}`;
    this.dashOffset = `${circumference * (1 - percentage)}`;
  }

  // ORG CHART
  zoomOrganizationChart(zoom: number): void {
    this.zoom = this.zoom + zoom;
  }

  itemClick(event: any): void {
    const toggleCssClass = (nodes: any[], targetId: string): void => {
      nodes.forEach((node) => {
        node.title = node.job_title;
        node.title = node.job_title + ' || ' + node.stage_name.toUpperCase();

        if (node.id === targetId) {
          node.cssClass = node.cssClass === 'selected' ? '' : 'selected';
        }

        //DEFINE CSS CLASS SELECT
        if (node.id === event.id && node.cssClass === 'selected') {
          node.points = node.points + 10;
        } else if (node.id === event.id && node.cssClass === '') {
          node.points = node.points - 10;
        }

        if (node.childs && node.childs.length > 0) {
          toggleCssClass(node.childs, targetId);
        }
      });
    };

    toggleCssClass(this.nodes, event.id);

    this.updateSelectedCount();
  }

  customizeOrgChart(): void {
    const toggleCssClass = (nodes: any[]): void => {
      nodes.forEach((node) => {
        node.title = node.job_title + ' || ' + node.stage_name.toUpperCase();
        if (node.childs && node.childs.length > 0) {
          toggleCssClass(node.childs);
        }
      });
    };

    toggleCssClass(this.nodes);
  }

  updateSelectedCount(): void {
    const countSelected = (nodes: any[]): number => {
      let count = 0;

      nodes.forEach((node) => {
        if (node.cssClass === 'selected') {
          count++;
        }
        if (node.childs && node.childs.length > 0) {
          count += countSelected(node.childs);
        }
      });

      return count;
    };

    this.selectedStaffCount = countSelected(this.nodes);
  }

  triggerAll(): void {
    const toggleCssClass = (nodes: any[]): void => {
      nodes.forEach((node) => {
        node.cssClass = 'selected';
        if (node.childs && node.childs.length > 0) {
          toggleCssClass(node.childs);
        }
      });
    };

    toggleCssClass(this.nodes);

    this.updateSelectedCount();
  }
}
